import { useEffect, useRef, useState } from 'react';
import { useI18n } from './i18n';
import { PlayIcon, PauseIcon } from './ControlIcons';

export default function MasterPlayButton({ playing, onPlay, speed, onSpeedChange, label, style }) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const root = useRef(null);
  const trigger = useRef(null);
  const hold = useRef(null);
  const suppressClick = useRef(false);
  const start = useRef(null);
  const clearHold = () => { clearTimeout(hold.current); hold.current = null; };
  const showMenu = () => { clearHold(); suppressClick.current = true; setOpen(true); };
  const closeMenu = () => { setOpen(false); trigger.current?.focus(); };

  useEffect(() => () => clearTimeout(hold.current), []);
  useEffect(() => {
    if (!open) return;
    root.current?.querySelector('[aria-checked="true"]')?.focus();
    const outside = (event) => { if (!root.current?.contains(event.target)) setOpen(false); };
    document.addEventListener('pointerdown', outside);
    return () => document.removeEventListener('pointerdown', outside);
  }, [open]);

  return <div ref={root} style={{ position: 'relative' }}>
    <button ref={trigger} type="button" aria-label={label}
      title={t('play.speedTitle', { label, speed: speed.toFixed(1) })}
      aria-haspopup="menu" aria-expanded={open}
      style={{ ...style, touchAction: 'none', WebkitTouchCallout: 'none', userSelect: 'none' }}
      onPointerDown={(event) => {
        if (event.button !== 0) return;
        clearHold(); suppressClick.current = false;
        start.current = { x: event.clientX, y: event.clientY };
        hold.current = setTimeout(showMenu, 500);
      }}
      onPointerMove={(event) => {
        if (start.current && Math.hypot(event.clientX - start.current.x, event.clientY - start.current.y) > 10) clearHold();
      }}
      onPointerUp={clearHold} onPointerCancel={clearHold} onPointerLeave={clearHold}
      onContextMenu={(event) => { event.preventDefault(); showMenu(); }}
      onKeyDown={(event) => {
        event.stopPropagation();
        if (event.key === 'ArrowUp' || event.key === 'ContextMenu' || (event.shiftKey && event.key === 'F10')) {
          event.preventDefault(); showMenu();
        }
        if (event.key === 'Enter' || event.key === ' ') suppressClick.current = false;
      }}
      onClick={() => {
        if (suppressClick.current) { suppressClick.current = false; return; }
        if (open) { setOpen(false); return; }
        onPlay?.();
      }}
    >{playing ? <PauseIcon /> : <PlayIcon />}</button>
    {/* Off-1× speed is visible at a glance — a corner badge on the existing
        button, so the current rate never hides inside the hold-menu. */}
    {speed !== 1 && <span aria-hidden="true" style={{
      position: 'absolute', top: -5, right: -5, zIndex: 1, pointerEvents: 'none',
      background: '#2563eb', color: '#fff', fontSize: 9, fontWeight: 800,
      lineHeight: '13px', padding: '0 4px', borderRadius: 7,
      boxShadow: '0 1px 4px rgba(0,0,0,0.5)',
    }}>{speed}×</span>}
    {open && <div role="menu" aria-label={t('play.speed')}
      onKeyDown={(event) => {
        event.stopPropagation();
        if (event.key === 'Escape') { event.preventDefault(); closeMenu(); }
        if (event.key === 'Tab') setOpen(false);
        if (['ArrowDown', 'ArrowUp', 'ArrowLeft', 'ArrowRight'].includes(event.key)) {
          event.preventDefault();
          const items = [...event.currentTarget.querySelectorAll('[role="menuitemradio"]')];
          const index = items.indexOf(document.activeElement);
          items[(index + (['ArrowUp', 'ArrowLeft'].includes(event.key) ? 3 : 1)) % items.length]?.focus();
        }
      }}
      style={{ position: 'absolute', bottom: 'calc(100% + 6px)', left: 0, width: 156,
        padding: 6, border: '1px solid #475569', borderRadius: 8, background: '#0f172a',
        boxShadow: '0 4px 18px #0008', color: '#e2e8f0' }}>
      <div style={{ fontSize: 11, marginBottom: 5 }}>{t('play.speed')}</div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4 }}>
        {[0.5, 1, 2, 4].map(value => <button key={value} type="button" role="menuitemradio"
          aria-checked={speed === value} onClick={() => { onSpeedChange?.(value); closeMenu(); }}
          style={{ height: 32, border: '1px solid #475569', borderRadius: 5, cursor: 'pointer',
            color: '#fff', background: speed === value ? '#2563eb' : '#1e293b' }}>
          {value.toFixed(1)}×
        </button>)}
      </div>
    </div>}
  </div>;
}
